import {useFocusEffect} from '@react-navigation/native'
import React, {FC, useCallback, useState} from 'react'

import {logErrorAndShowException} from 'src/lib/errors'
import {BaseTemplate} from 'src/products/general/components/templates/BaseTemplate/BaseTemplate'
import {AnalyzeIdDocumentDocument} from 'src/products/loans/IDScan/AnalyzeIDDocument.gqls'
import {IDScanCapturePhases, IDScanProcessingStatuses} from 'src/products/loans/IDScan/IDScan.types'
import {IDScanTemplate} from 'src/products/loans/IDScan/IDScanTemplate'
import {useGetValidDriverLicenses} from 'src/products/loans/IDScan/useGetValidDriverLicenses'
import {useCassandraMutation} from '@possible/cassandra/src/utils/hooks'
import {AnalyzeIdDocumentFailureCodes} from 'src/cassandra'
import {OnCameraCompleteCallback} from 'src/products/general/components/organisms/CameraPage/CameraPage'

type Props = {
  onComplete: () => void | Promise<void>
}

export const IDScanGQLContainer: FC<Props> = (props) => {
  const {onComplete} = props
  const [isBusy, setIsBusy] = useState<boolean>(false)
  const [error, setError] = useState<AnalyzeIdDocumentFailureCodes | undefined>(undefined)
  const [processingStatus, setProcessingStatus] = useState<IDScanProcessingStatuses | undefined>()
  const [wasDLScanned, setWasDLScanned] = useState<boolean>(false)
  const {getDLDocuments, isLoading} = useGetValidDriverLicenses()

  const [analyzeIDDocument] = useCassandraMutation(AnalyzeIdDocumentDocument)

  useFocusEffect(
    useCallback(() => {
      if (!isLoading && !wasDLScanned) {
        void getDLDocuments().then((docs) => {
          setWasDLScanned(docs.length > 0)
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  )

  // Displays the processing BottomSheet
  const handleOnImageSubmitStart = (): void => {
    // the actual upload of the image is handled in IDCameraPage
    setIsBusy(true)
    setProcessingStatus(IDScanProcessingStatuses.Processing)
  }

  // Displays the 'Success' BottomSheet
  const handleOnImageSubmitComplete: OnCameraCompleteCallback = async (
    image,
    orientation,
    documentId,
  ) => {
    try {
      if (documentId === undefined || documentId === null) {
        throw new Error('No document ID provided')
      }

      const {data} = await analyzeIDDocument({
        variables: {
          documentId: documentId,
        },
      })

      if (data === null || data === undefined) {
        throw new Error('No data returned from analyzeIDDocument')
      } else if (data.analyzeIDDocument.result === true) {
        setProcessingStatus(IDScanProcessingStatuses.Complete)
      } else if (
        data.analyzeIDDocument.result === false &&
        data.analyzeIDDocument.failureCodes.length > 0
      ) {
        setProcessingStatus(undefined)
        // The error message is displayed in a modal - so we can only display one error at a time
        setError(data.analyzeIDDocument.failureCodes[0])
      } else {
        throw new Error('No data returned from analyzeIDDocument')
      }
    } catch (e) {
      void logErrorAndShowException(e, 'Error while calling analyzeIDDocument')
    }
  }

  // Called when the 'Continue' button on the 'Success!' Bottom Sheet is pressed
  const handleOnBottomSheetContinue = async (): Promise<void> => {
    try {
      setIsBusy(true)
      await onComplete()
    } catch (e) {
      if (e instanceof Error) {
        void logErrorAndShowException(e, '[Workflow Error] Error while trying to continue IDScan ')
      }
    } finally {
      setProcessingStatus(undefined)
      setIsBusy(false)
    }
  }

  return (
    <BaseTemplate isLoading={isLoading}>
      <IDScanTemplate
        onImageSubmitStart={handleOnImageSubmitStart}
        onImageSubmitComplete={handleOnImageSubmitComplete}
        onContinue={handleOnBottomSheetContinue}
        busy={isBusy}
        phase={wasDLScanned ? IDScanCapturePhases.ImageCaptured : IDScanCapturePhases.ImageCapture}
        error={error}
        processingStatus={processingStatus}
      />
    </BaseTemplate>
  )
}
